import { useContext } from 'react'
import {
  AgxVendorBuyerDetails,
  AgxSolicitorDetails,
  EditScope,
  StakeholderType,
  VendorBuyerType,
} from '@urbanx/agx-ui-components'
import { AuthContext } from '@urbanx/agx.auth.client'
import { AgxSpacer, AgxTypography } from '@urbanx/agx.ui-components2'
import Panel from 'components/panel/Panel'
import { deleteFile, getFileLink, uploadFile } from 'services'
import {
  CampaignDetailModel,
  SolicitorDetails,
  VendorModel,
} from '@urbanx/agx.campaigns.client'

interface VendorDetailsSectionProps {
  campaign?: CampaignDetailModel | undefined
  vendorDetails: VendorModel[]
  setVendorDetails: (value: VendorModel[]) => void
  solicitorDetails: SolicitorDetails | undefined
  setSolicitorDetails: (value: SolicitorDetails | undefined) => void
  hasUnsavedChanges: boolean
}

const VendorDetailsSection = ({
  campaign,
  vendorDetails,
  setVendorDetails,
  solicitorDetails,
  setSolicitorDetails,
  hasUnsavedChanges,
}: VendorDetailsSectionProps) => {
  const { getAuthToken } = useContext(AuthContext)

  return (
    <AgxSpacer id="vendor-details" direction="vertical" gap="spacing-xl" fill>
      <Panel
        title={
          <AgxTypography id="vendorDetailsTitle" type="body" size="medium">
            Vendor Details
          </AgxTypography>
        }
        padding={{
          top: 'md',
          bottom: 'md',
          left: 'xl',
          right: 'xl',
        }}
      >
        <AgxSpacer direction="vertical" gap="spacing-xl" fill>
          <AgxVendorBuyerDetails
            key={`VendorDetails-${vendorDetails.length}`}
            id="vendorDetails"
            getAuthToken={getAuthToken}
            uploadFile={uploadFile}
            deleteFile={deleteFile}
            getFileLink={getFileLink}
            onValueChanged={({ value }) => setVendorDetails(value ?? [])}
            propertyAddress={campaign?.address}
            defaultValue={vendorDetails}
            editScope={EditScope.FullEdit}
            stakeholderType={StakeholderType.Vendor}
            vendorBuyerTypes={[
              VendorBuyerType.Individual,
              VendorBuyerType.PowerOfAttorney,
              VendorBuyerType.DeceasedEstate,
              VendorBuyerType.Company,
            ]}
            companyVendorBuyerTypes={[
              VendorBuyerType.Individual,
              VendorBuyerType.PowerOfAttorney,
              VendorBuyerType.DeceasedEstate,
              VendorBuyerType.Company,
            ]}
            vendorBuyerConfig={{
              preferredNameRequired: true,
              emailRequired: true,
              phoneNumberRequired: true,
              addressRequired: false,
              excludeMiddleName: false,
              displayAddCc: false,
            }}
            powerOfAttorneyConfig={{
              hideDocumentationUpload: true,
            }}
            companyConfig={{
              hideAbn: false,
              hideAcn: false,
              hideRegisteredForGst: false,
            }}
            required
            campaignId={campaign?.campaignId || ''}
            displayLegalName={false}
            agents={[]}
            leadAgentId=""
            validate={hasUnsavedChanges}
          />
        </AgxSpacer>
      </Panel>
      <Panel
        title={
          <AgxTypography id="vendorSolicitorTitle" type="body" size="medium">
            Vendor Solicitor
          </AgxTypography>
        }
        padding={{
          top: 'md',
          bottom: 'md',
          left: 'xl',
          right: 'xl',
        }}
      >
        <AgxSpacer direction="vertical" gap="spacing-xl" fill>
          <AgxSolicitorDetails
            key={`VendorSolicitorDetails-${solicitorDetails?.solicitorType}`}
            id="vendorSolicitorDetails"
            onValueUpdated={({ value }) => setSolicitorDetails(value)}
            defaultValue={solicitorDetails}
            label=""
            solicitorLabel="Solicitor"
            defaultConveyUSolicitorInfo={null}
            showToBeAdvisedOption
            required
            validate={hasUnsavedChanges}
          />
        </AgxSpacer>
      </Panel>
    </AgxSpacer>
  )
}

export default VendorDetailsSection
